import { ComponentType } from '@angular/cdk/portal';
import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { AgNoRowsOverlayComponent } from '../../components/ag-grid-components/ag-no-rows-overlay/ag-no-rows-overlay.component';
import { AgIconRendererComponent } from '../../components/ag-grid-components/ag-icon-renderer/ag-icon-renderer.component';
import { ColDef, GetRowIdFunc, GetRowIdParams } from 'ag-grid-community';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { ColGroupDef, RowClassRules, RowDragEndEvent, RowEditingStoppedEvent, RowSelectionOptions } from 'ag-grid-community';
import { TemplateService } from '../../services/settings/template.service';
import { TemplateConfigFieldItem } from '../../models/template-config-field-item';
import { FieldFilterService } from '../../services/filters/field-filter.service';
import { Subscription } from 'rxjs';
import { TemplateConfigItem } from '../../models/template-config-item';
import { FilterSelectComponent } from '../../components/field-components/filter-select/filter-select.component';
import { BooleanSelectComponent } from '../../components/field-components/static/boolean-select/boolean-select.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AgHeaderInfoIconRendererComponent } from '../../components/ag-grid-components/ag-header-info-icon-renderer/ag-header-info-icon-renderer.component';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../confirm-dialog/confirm-dialog.component';
import { AgLoadingOverlayComponent } from '../../components/ag-grid-components/ag-loading-overlay/ag-loading-overlay.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDivider } from '@angular/material/divider';
import { MatButton } from '@angular/material/button';
import { TemplateConfigStore } from '../../store/template-config.store';
import { FilterSelectItem } from '../../models/filter-select-item';
import { AgSingleSelectComponent } from '../../components/ag-grid-components/ag-single-select/ag-single-select.component';
import { NumberCellEditorComponent } from '../../components/ag-grid-components/number-cell-editor/number-cell-editor.component';

export class AddUpdateTemplateConfigDialogModel {
  readonly component: ComponentType<any> = AddUpdateTemplateConfigDialogComponent;
  panelClass?: string | string[] = 'dialog-container';
  autoFocus?: boolean = false;
  data?: any;
  disableClose? = true;
  maxWidth? = '95dvw';

  constructor(data: any) {
    this.data = data;
  }
}

@Component({
    selector: 'app-add-update-template-config-dialog',
    templateUrl: './add-update-template-config-dialog.component.html',
    styleUrls: ['./add-update-template-config-dialog.component.scss'],
    imports: [
        MatDialogTitle,
        CdkScrollable,
        MatDialogContent,
        MatFormField,
        MatInput,
        FormsModule,
        ReactiveFormsModule,
        MatError,
        BooleanSelectComponent,
        FilterSelectComponent,
        MatDivider,
        AgGridModule,
        MatDialogActions,
        MatButton
    ],
    providers: [TemplateConfigStore]
})
export class AddUpdateTemplateConfigDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  noRowsOverlayComponent: any = AgNoRowsOverlayComponent;
  noRowsOverlayComponentParams: any = {
    noRowsMessageFunc: () => {
      return 'No Entries Found'
    },
  };

  loadingOverlayComponent: any = AgLoadingOverlayComponent;

  frameworkComps = {
    iconRenderer: AgIconRendererComponent,
    headerInfoRenderer: AgHeaderInfoIconRendererComponent,
    agSingleSelect: AgSingleSelectComponent
  }

  get formatOptions() {
    return this.store.formatOptions(); // This returns the current array 
  }

  // Expose the signal value for use in the template
  get fieldItemsSignal() {
    return this.store.fieldItems();
  }

  selectedGridColDefs: (ColDef | ColGroupDef)[] = [
    {
      headerName: 'Selected Fields',
      suppressMovable: true,
      children: [
        { rowDrag: true, maxWidth: 40, suppressMovable: true,
          tooltip: (params) => {
            return 'Move'
          }, 
        },
        { field: 'SortID', headerName: 'Order', minWidth: 57, maxWidth: 57, suppressMovable: true},
        { field: 'Label', headerName: 'Label', editable: true, suppressMovable: true},
        { field: 'Field', headerName: 'Field', suppressMovable: true},
        { field: 'DefaultValue', headerName: 'Prefill', suppressMovable: true,
          editable: (params) => {
            return params.data.FieldID == 1
          },
          headerComponent: 'headerInfoRenderer',
          headerComponentParams: {
            iconTooltip: `If field is of type "<Blank>", enter the text that should auto populate.`
          }
        },
        {
          field: 'FormatID',
          headerName: 'Formatting',
          minWidth: 110,
          maxWidth: 110,
          suppressMovable: true,
          editable: (params) => {
            return this.formatOptions.filter(o => o.FieldDataType === params.data?.DataType).length > 0;
          },
          cellEditor: 'agSingleSelect',
          cellEditorParams: (params) => ({
            opts: this.formatOptions.filter(o => o.FieldDataType === params.data?.DataType),
          }),
          valueFormatter: (p) => {
            const opts = this.formatOptions.filter(o => o.FieldDataType === p.data?.DataType);
            return opts.find(o => o.ID === Number(p.value))?.Name ?? '';
          }
        },
        {
          field: 'MaxLength',
          headerName: 'Max Length',
          suppressMovable: true,
          minWidth: 120,
          maxWidth: 120,
          editable: (params) =>
            !!params.data &&
            params.data.FieldID > 1 &&
            params.data.DataType?.toLowerCase().startsWith('alpha'),
          cellEditor: NumberCellEditorComponent,
          headerComponent: 'headerInfoRenderer',
          headerComponentParams: {
            iconTooltip: `Max number of characters allowed for this field. Applies to alpha or alphanumeric fields only.`
          }
        },
        { 
          cellRenderer: 'iconRenderer',
          cellRendererParams: {
            icon: 'delete'
          },
          suppressMovable: true,
          tooltip: (params) => {
            return 'Delete'
          }, 
          onCellClicked: (params) => {
            if (params.event) {
              params.event.stopPropagation();
              params.event.preventDefault();
            }
            this.onRemoveRow(params);
            return false;
          },
          maxWidth: 40
        }
      ]
    },
  ];

  availableGridColDefs: (ColDef | ColGroupDef)[] = [
    {
      headerName: 'Available Fields',
      children: [
        { rowDrag: true, maxWidth: 50, suppressMovable: true,
          tooltip(params) {
            return 'Move'
          }, 
        },
        { field: 'Field', headerName: 'Field', suppressMovable: true },
        { field: 'Category', headerName: 'Category', maxWidth: 145, suppressMovable: true },
      ]
    }
  ];

  // Matches the pre-v32 rowSelection='multiple' behaviour: click/ctrl-click selection, no checkboxes.
  rowSelection: RowSelectionOptions = { mode: 'multiRow', checkboxes: false, headerCheckbox: false, enableClickSelection: true };

  defaultColDef: ColDef = {
    editable: false,
    sortable: true,
    filter: true,
    menuTabs: [],
    wrapHeaderText: true,
    autoHeaderHeight: true,
    minWidth: 100
  };

  selectedGridDefaultColDef: ColDef = {
    editable: false,
    sortable: false,
    menuTabs: [],
    wrapHeaderText: true,
    autoHeaderHeight: true,
    minWidth: 100,
    tooltip: (params) => {
      return 'Double Click to Edit';
    }
  };

  selectedGridOptions = {
    onRowDragEnd: this.onRowDragEnd.bind(this),
    onGridReady: this.onSelectedGridReady.bind(this),
    onRowEditingStopped: this.onRowEditingStopped.bind(this),
    editType: 'fullRow' as const,
    singleClickEdit: false,
    onRowDoubleClicked: (event) => {
      // Check if the click was on the delete button
      if (event.column && event.column.getColDef) {
        const clickedColumn = event.column.getColDef();
        if (clickedColumn.cellRenderer === 'iconRenderer' && 
            clickedColumn.cellRendererParams?.icon === 'delete') {
          return; // Don't trigger edit mode for delete button clicks
        }
      }
      this.onEditRow(event);
    }
  };  
  
  public rowClassRules: RowClassRules = {
    // row style function
    'danger-cell-2': (params) => {
      let invalid =
        (!params.data.Label || params.data.Label.trim().length === 0) ||
        (this.store.currentTemplate().TypeID === this.asmTypeID &&
          params.data.FieldID > 1 &&
          params.data.DataType?.toLowerCase().startsWith("alpha") &&
          (!params.data.MaxLength || params.data.MaxLength <= 0));

      //Grid check for create button disable
      params.api?.forEachNode(n => {
        if (
          (!n.data.Label || n.data.Label.trim().length === 0) ||
          (this.store.currentTemplate().TypeID === this.asmTypeID &&
            n.data.FieldID > 1 &&
            n.data.DataType?.toLowerCase().startsWith("alpha")) &&
            (!n.data.MaxLength || n.data.MaxLength <= 0)
        ) {
          params.context.componentParent.invalidSelectedRows = true;
        }

        /// IF UNIQUE LABEL ///
        if(n.rowIndex != params.rowIndex && n.data.FieldID == params.data.FieldID && n.data.Label == params.data.Label) {
          params.context.componentParent.invalidSelectedRows = true;
          invalid = true;
        }
      });

      params.context.componentParent.checkGridValidation();

      return invalid;
    }
  };

  gridContext: any;

  actionType = '';
  templateID = 0;
  types: FilterSelectItem[] = [];

  invalidSelectedRows = false;
  missingLabel = false;
  missingMaxLength = false;
  currentSelectedCount = 0;

  form: FormGroup;
  nameControl = new FormControl('', [Validators.required]);
  notesControl = new FormControl('');

  nameControlName = 'name';
  notesControlName = 'notes';
  activeControlName = 'active';

  availableItems: TemplateConfigFieldItem[] = [];

  invalidTemplateNames: string[] = [];

  subs: Subscription[] = [];
  
  readonly store = inject(TemplateConfigStore);
  readonly data = inject(MAT_DIALOG_DATA);
  selectedItems: TemplateConfigFieldItem[];

  asmTypeID?: number;

  @ViewChild('selectedFields') selectedGrid!: AgGridAngular;
  @ViewChild('availableFields') availableGrid!: AgGridAngular;
  @ViewChild('activeSelect') activeSelect: BooleanSelectComponent;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.selectedGrid?.api?.sizeColumnsToFit();
    this.availableGrid?.api?.sizeColumnsToFit();
  }

  constructor(
    public dialogRef: MatDialogRef<AddUpdateTemplateConfigDialogComponent>,
    private dialog: MatDialog,
    private templateService: TemplateService,
    private filterService: FieldFilterService,
    private fb: FormBuilder,
    private snackbar: MatSnackBar
  ) {
    this.gridContext = { componentParent: this };
    this.form = this.fb.group({});
    this.actionType = this.data?.actionType;
    this.templateID = this.data?.item?.ID || 0;
  }

  ngOnInit(): void {
    this.form.addControl(this.nameControlName, this.nameControl);
    this.form.addControl(this.notesControlName, this.notesControl);
    
    this.subs.push(this.filterService.templateConfigTypes.subscribe(result => {
      this.asmTypeID = result.find(t => t.Description === 'ASM')?.ID;
      this.types = result;
    }));

    this.subs.push(this.nameControl.valueChanges.subscribe(input => {
      if(!input) {
        this.nameControl.setErrors({required: true});
        return;
      }
    }));
  }

  ngAfterViewInit(): void {
    if(this.actionType == 'edit') {
      this.store.setIsNew(false);
      this.store.setOriginalTemplate(this.data.item as TemplateConfigItem);

      this.nameControl.setValue(this.data.item.Name);
      this.notesControl.setValue(this.data.item.Notes);
      this.activeSelect.setValue(this.data.item.Enabled);
      this.form.markAsTouched();
    } else {
      this.store.setIsNew(true);
      this.store.setOriginalTemplate(new TemplateConfigItem());
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  loadData() {
    this.templateService.getAvailableFields("").subscribe(result => {
      this.availableItems = result;
      this.availableGrid?.api?.sizeColumnsToFit();
    });
    this.selectedGrid?.api?.sizeColumnsToFit();
  }

  onSelectedGridReady(params) {
    this.addGridDropZone();
  }

  onAvailableGridReady(params) {
    this.loadData();
  }

  getAvailableGridRowID: GetRowIdFunc = (params: GetRowIdParams) => {
    return String(params.data.FieldID);
  };

  getSelectedGridRowID: GetRowIdFunc = (params: GetRowIdParams) => {
    return String(params.data.SortID);
  };

  addGridDropZone() {
    const dropZoneParams = this.selectedGrid?.api?.getRowDropZoneParams({});

    this.availableGrid?.api?.addRowDropZone(dropZoneParams);
  }

  updateSelectedSortOrder(params, index, isRemove: boolean = false, isReorder?: boolean) {
    if (isRemove) {
      return this.store.addRemoveFieldItems(index);
    } else {
      return this.store.addRemoveFieldItems(index, params.node.data, isReorder);
    }
  }

  onEditRow(params: any) {
    const element = params.event?.target as HTMLElement | null;
    if (element && element.closest('input, select, textarea, .ag-cell-inline-editing')) {
      return;
    }

    params.api?.startEditingCell({
      rowIndex: params.node.rowIndex,
      colKey: 'Label',
    });
  }


  onRowEditingStopped(params: RowEditingStoppedEvent) {
    this.store.updateFieldItem(params.data as TemplateConfigFieldItem);
  }

  onRemoveRow(params) {
    params.context.componentParent.updateSelectedSortOrder(params, params.node.rowIndex, true).subscribe((result: any) => {
      if (!result) {
        this.setInvalidNameError();
      }
    });
  }

  setInvalidNameError() {
    this.invalidTemplateNames.push(this.nameControl.value);
    this.nameControl.setErrors({invalidName: true, required: false});
    this.nameControl.markAsTouched();
  }

  setRequiredNameError() {
    this.invalidTemplateNames.push(this.nameControl.value);
    this.nameControl.setErrors({invalidName: false, required: true});
    this.nameControl.markAsTouched();
  }
  
  onSaveClick(closeAfter: boolean) {
    const name = this.nameControl.value;
    const notes = this.notesControl.value;
    const active = this.activeSelect.getSelectedValueAsBoolean();
    const toSave = this.store.currentTemplate();
    toSave.Enabled = active ?? false;
    toSave.Name = name ?? "";
    toSave.Notes = notes ?? "";
    this.store.setCurrentTemplate(toSave).then(() => {
      try {
        this.store.saveCurrentTemplate().subscribe((result: any) => {
          if (!result) {
            this.snackbar.open("Failed to Save", undefined, {duration: 3000});
          } else if (result == 'invalid-name') {
            this.setInvalidNameError();
            this.snackbar.open("Failed to Save", undefined, {duration: 3000});
          } else {
            this.snackbar.open("Template Saved", undefined, {duration: 3000});
            if (closeAfter) {
              this.dialogRef.close(true);
            } else {
              this.store.setOriginalTemplate(this.store.currentTemplate());
              if (this.store.isNew()) {
                this.store.setIsNew(false);
              }
            }
          }
        });
      } catch (error) {
        if(error == "InvalidName") {
          this.setInvalidNameError();
        }
        this.snackbar.open("Failed to Save", undefined, {duration: 3000});
      }
    }).catch((error) => {
      console.log(error);
    });
  }

  openCancelConfirm() {
    const message = 'Clicking Cancel will not save any changes. Do you want to continue to close and not save?';
    const title = 'Warning';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "450px",
      data: new ConfirmDialogModel(title, message, '')
    });

    this.subs.push(dialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.store.revertToOriginalTemplate();
        this.dialogRef.close(true);
      }
    }));
  }

  checkGridValidation() {
    let result = false;
    this.missingLabel = false;
    this.missingMaxLength = false;
    this.selectedGrid?.api?.forEachNode(n => {
      if(!n.data.Label || n.data.Label.trim().length === 0) {
        this.missingLabel = true;
        result = true;
      }
      if (
        this.store.currentTemplate().TypeID === this.asmTypeID &&
        n.data.FieldID > 1 &&
        n.data.DataType?.toLowerCase().startsWith("alpha") &&
        (!n.data.MaxLength || n.data.MaxLength <= 0)
      ) {
        this.missingMaxLength = true;
        result = true;
      }
    })

    this.invalidSelectedRows = result;
  }

  isValid() {
    return !this.form.invalid && !this.invalidSelectedRows && this.store?.fieldItems()?.length > 0
  }

  onRowDragEnd(event: RowDragEndEvent) {
    let index;
    if (event.overIndex == -1) {
      index = this.store.fieldItems() && this.store.fieldItems().length > 0 ? this.store.fieldItems().length : 0;
    } else if (event.overIndex != null && event.overIndex != undefined) {
      index = event.overIndex;
    } else {
      index = this.store.fieldItems().findIndex(item => item.ID == event.node.data.ID);
    }
    const itemInState = this.store.fieldItems()?.find((item) => item?.ID == event.node?.data?.ID);
    this.updateSelectedSortOrder(event, index, false, itemInState != null && itemInState != undefined)
      .subscribe((result: any) => {
        if (!result) {
          this.setInvalidNameError();
        }
        if (!itemInState) {
          // Wait for the next event cycle to ensure the grid has updated
          setTimeout(() => {
            // Get all row nodes and find the one at our index
            const rowNode = this.selectedGrid?.api?.getDisplayedRowAtIndex(index);
            const rowIndex = rowNode?.rowIndex != null ? rowNode.rowIndex : -1;
            if (rowNode && rowIndex >= 0) {
              this.selectedGrid?.api?.startEditingCell({
                rowIndex: rowIndex,
                colKey: 'Label'
              });
            }
          });
        }
      });
  }
  
  clearNameError() {
    this.nameControl.setErrors(null);
    this.nameControl.markAsUntouched();
    this.nameControl.markAsPristine();
  }

  validateName(changed: any) {
    if (changed) {
      const trimmedName = this.nameControl.value?.trim();
      if (trimmedName && trimmedName.length > 0) {
        this.nameControl.setValue(trimmedName);
        //Update Store
        const currentTemplate = this.store.currentTemplate();
        currentTemplate.Name = trimmedName;
        this.store.setCurrentTemplate(currentTemplate).then(() => {
          this.store.saveCurrentTemplate().subscribe({
            next: (result: any) => {
              if (result == 'invalid-name') {
                this.setInvalidNameError();
              } else if (!result) {
                this.snackbar.open("Error validating input.", undefined, {duration: 3000});
              } else {
                this.nameControl.setErrors(null);
              }
            },
            error: (error) => {
              if(error == "InvalidName") {
                this.setInvalidNameError();
              }
            }
          });
        });
      } else if (!trimmedName || trimmedName.length == 0) {
        this.setRequiredNameError();
      }
    }
  }
}
